import { FC, KeyboardEvent, ReactNode, useEffect, useMemo, useState } from "react";

import { MAX_TEST_EMAIL_RECIPIENTS } from "@simplyk/common";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useForm } from "react-hook-form";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { hasLessOrEqualElementsThan } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ActiveOrganizationMember } from "@/common/types/trpc";
import { DashboardAutocompleteV2 } from "@/dashboard-design-system/DashboardAutocompleteV2/DashboardAutocompleteV2";
import { DashboardDialog } from "@/dashboard-design-system/Dialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Mail } from "@/icons/outlined";

type RecipientOption = { label: string; value: string };
type RecipientValue = string | RecipientOption;
type RecipientsForm = { recipients: RecipientValue[] };

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getRecipientEmail = (recipient: RecipientValue) =>
  (typeof recipient === "string" ? recipient : recipient.label).trim();

interface SendPreviewEmailDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (emails: string[]) => void | boolean | Promise<void | boolean>;
  activeOrganizationMembers: ActiveOrganizationMember[] | undefined;
  activeOrganizationMembersLoading: boolean;
  title?: ReactNode;
  description?: ReactNode;
  allowCustomEmail?: boolean;
}

export const SendPreviewEmailDialog: FC<SendPreviewEmailDialogProps> = ({
  open,
  onClose,
  onSend,
  activeOrganizationMembers,
  activeOrganizationMembersLoading,
  title,
  description,
  allowCustomEmail = true,
}) => {
  const { t } = useTranslate();
  const [inputError, setInputError] = useState<string | undefined>(undefined);
  const [inputValue, setInputValue] = useState<string>("");
  const { currentUser, isSendingEmailForbidden, connectedAsAdministrator } = useCurrentUserContext();

  const adminMember = connectedAsAdministrator ? activeOrganizationMembers?.[0] : undefined;
  const defaultRecipient = useMemo<RecipientValue[]>(() => {
    if (adminMember) {
      return [{ label: adminMember.email || "", value: adminMember.id }];
    }
    return currentUser?.email ? [{ label: currentUser.email, value: currentUser.id }] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMember?.id, currentUser?.id]);

  const form = useForm<RecipientsForm>({
    mode: "onChange",
    defaultValues: { recipients: defaultRecipient },
  });

  const {
    formState: { errors, isValid: isFormValid },
  } = form;

  const handleInputKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (allowCustomEmail && event.key === "Enter" && inputValue.trim()) {
      event.preventDefault();
      event.stopPropagation();
      commitTypedEmail(inputValue, { showError: true });
    }
  };

  const commitTypedEmail = (email: string, { showError }: { showError: boolean }) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setInputError(undefined);
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      if (showError) {
        setInputError(t("dashboard", "emailEditor.sendTest.invalidEmail"));
      }
      return;
    }

    const currentRecipients = form.getValues("recipients") || [];
    const alreadyExists = currentRecipients.some(
      (recipient) => getRecipientEmail(recipient).toLowerCase() === trimmedEmail.toLowerCase()
    );

    if (!alreadyExists) {
      form.setValue("recipients", [...currentRecipients, { label: trimmedEmail, value: trimmedEmail }], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    setInputValue("");
    setInputError(undefined);
  };

  const handleSubmit = form.handleSubmit(async ({ recipients }) => {
    if (isSendingEmailForbidden) {
      return;
    }

    const typedEmail = allowCustomEmail ? inputValue.trim() : "";
    const emails = Array.from(
      new Set(
        [...recipients.map(getRecipientEmail), typedEmail].filter(
          (email) => email.length > 0 && EMAIL_REGEX.test(email)
        )
      )
    );

    if (emails.length === 0) {
      return;
    }

    const didSend = await onSend(emails);
    if (didSend !== false) {
      onClose();
    }
  });

  useEffect(() => {
    if (open) {
      setInputValue("");
      setInputError(undefined);
      form.reset({ recipients: defaultRecipient });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const recipients = form.watch("recipients");

  const options = useMemo<RecipientOption[]>(() => {
    const orgMemberOptions =
      activeOrganizationMembers?.map((member) => ({ label: member.email || "", value: member.id })) || [];

    const enteredEmailOptions = (recipients || [])
      .map((recipient) => getRecipientEmail(recipient))
      .filter((email) => email.length > 0)
      .map((email) => ({ label: email, value: email }));

    const allOptions = [...orgMemberOptions, ...enteredEmailOptions];

    return Array.from(new Map(allOptions.map((opt) => [opt.label, opt])).values());
  }, [activeOrganizationMembers, recipients]);

  const isRequiredMessage = t("dashboard", "common.isRequired");

  const recipientsRules = [
    hasLessOrEqualElementsThan(
      MAX_TEST_EMAIL_RECIPIENTS,
      t("dashboard", "emailEditor.sendTest.tooManyRecipients", { max: MAX_TEST_EMAIL_RECIPIENTS })
    ),
    {
      validate: {
        atLeastOneValidEmail: (recipients?: RecipientValue[]) =>
          (recipients || []).some((recipient) => EMAIL_REGEX.test(getRecipientEmail(recipient)))
            ? true
            : isRequiredMessage,
        allEmailsValid: (recipients?: RecipientValue[]) =>
          (recipients || []).every((recipient) => EMAIL_REGEX.test(getRecipientEmail(recipient)))
            ? true
            : t("dashboard", "emailEditor.sendTest.invalidEmail"),
      },
    },
  ];

  const recipientsErrorMessage =
    errors["recipients"]?.message === isRequiredMessage ? undefined : errors["recipients"]?.message;

  return (
    <DashboardDialog
      icon={<Mail />}
      open={open}
      onClose={onClose}
      slots={{
        title: (
          <DashboardTypography variant="h6" vibe="text-neutral-intense">
            {title ?? t("dashboard", "emailEditor.sendTest.title")}
          </DashboardTypography>
        ),
      }}
      actions={{
        main: {
          children: t("dashboard", "common.send"),
          onClick: handleSubmit,
          disabled: isSendingEmailForbidden || activeOrganizationMembersLoading || !isFormValid,
        },
        cancel: {
          onClick: onClose,
        },
      }}
      maxWidth="xs"
      fullWidth
    >
      <Stack gap={2}>
        {description ? (
          <DashboardTypography variant="body2" vibe="text-neutral-moderate">
            {description}
          </DashboardTypography>
        ) : null}
        <Box onKeyDownCapture={handleInputKeyDown}>
          <DashboardAutocompleteV2
            name="recipients"
            control={form.control}
            rules={recipientsRules}
            label={t("dashboard", "signup.emailAddress")}
            placeholder={t("dashboard", "emailEditor.sendTest.recipientsPlaceholder")}
            errorMessage={inputError || recipientsErrorMessage}
            disabled={isSendingEmailForbidden || activeOrganizationMembersLoading}
            options={options}
            inputValue={inputValue}
            onInputChange={(_event, value, reason) => {
              if (reason !== "reset") {
                setInputError(undefined);
              }
              setInputValue(value);
            }}
            onBlur={() => {
              if (allowCustomEmail) {
                commitTypedEmail(inputValue, { showError: false });
              }
            }}
            multiple
            freeSolo={allowCustomEmail}
            disableCloseOnSelect
            hideClearIcon
            limitTags={-1}
            isOptionEqualToValue={(option, value) => {
              const optionEmail = typeof option === "string" ? option : option.label;
              const valueEmail = typeof value === "string" ? value : value.label;
              return optionEmail.toLowerCase() === valueEmail.toLowerCase();
            }}
            sx={{
              "& .MuiInputBase-root": {
                flexWrap: "wrap !important",
                alignItems: "flex-start !important",
                minHeight: "56px",
                height: "auto",
                paddingTop: 1,
                paddingBottom: 1,
                gap: 0.5,
              },
              "& .MuiInputBase-input": {
                flexBasis: "100%",
                width: "100%",
                minWidth: "100px",
                marginTop: 0.5,
                caretColor: "auto !important",
              },
            }}
          />
        </Box>
      </Stack>
    </DashboardDialog>
  );
};
